import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

export default function NotificationsInbox() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/notifications`);
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const payload = await res.json();
      setItems(Array.isArray(payload?.items) ? payload.items : []);
      setUnread(Number(payload?.unread || 0));
    } catch (err) {
      toast.error("Could not load notifications", { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (id) => {
    try {
      await fetch(`${API_BASE}/api/admin/notifications/${encodeURIComponent(id)}/read`, { method: "POST" });
      setItems((rows) => rows.map((r) => (String(r?.id) === String(id) ? { ...r, read: true } : r)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {
      toast.error("Could not mark as read");
    }
  };

  const markAllRead = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/notifications/read-all`, { method: "POST" });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setItems((rows) => rows.map((r) => ({ ...r, read: true })));
      setUnread(0);
      toast.success("All notifications marked read");
    } catch (err) {
      toast.error(err?.message || "Could not update");
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[900px] mx-auto" data-testid="notifications-inbox">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">Workspace</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium flex items-center gap-3">
            <Bell size={28} strokeWidth={1.5} className="text-[var(--primary)]" />
            Notifications
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            {loading ? "Loading…" : `${unread} unread · ${items.length} total`}
          </p>
        </div>
        <button type="button" className="btn-secondary text-sm" onClick={markAllRead} disabled={loading || unread === 0}>
          <CheckCheck size={14} /> Mark all read
        </button>
      </div>

      <div className="mt-10 surface divide-y divide-[var(--border-default)]">
        {items.length === 0 && !loading ? (
          <div className="p-8 text-sm text-[var(--text-tertiary)] text-center">No notifications yet.</div>
        ) : null}
        {items.map((row) => {
          const id = String(row?.id || "");
          const href = row?.href || row?.deepLink || "";
          const inner = (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className={`text-sm ${row?.read ? "text-[var(--text-secondary)]" : "text-[var(--text-primary)]"}`}>{row?.title || "Notice"}</div>
                  {row?.body ? <div className="text-xs text-[var(--text-tertiary)] mt-1">{row.body}</div> : null}
                  <div className="text-[10px] font-mono text-[#52525B] mt-2">{row?.ts || ""}</div>
                </div>
                {!row?.read ? (
                  <button type="button" className="btn-secondary text-[10px] shrink-0" onClick={() => markRead(id)}>
                    Mark read
                  </button>
                ) : (
                  <span className="text-[10px] font-mono text-[#52525B] uppercase tracking-widest">Read</span>
                )}
              </div>
            </>
          );
          if (href) {
            return (
              <Link key={id} to={href} className="block p-5 hover:bg-[var(--bg-elev)] transition">
                {inner}
              </Link>
            );
          }
          return (
            <div key={id} className="p-5">
              {inner}
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-xs text-[var(--text-tertiary)] font-mono">
        Bell dropdown shows the six most recent items. This page lists the full feed (up to 200). Deep links render when notifications include an <code className="text-[var(--text-secondary)]">href</code> field.
      </p>
    </div>
  );
}
